import { environment, injectScript } from '@chilipiper/web-data-external'
import { getEnv } from '../shared/env'
import { withTimeout } from '../shared/helpers'
import { getMode, logger } from './logger'
import { jitsu, flushQueue } from './jitsuWrapper'
import { onRouteChange } from './routeChange'
import { initEngagementTracking } from './engagementTracking'
import { initFileDownloadTracking } from './fileDownloadTracking'
import { initFormTracking } from './formTracking'
import { trackEmailFromForm } from './emailTracking'

type InitJitsuParams = {
  tenantId: string
}

// Module-level ref so the onRouteChange callback (registered once) always
// flushes the current engagement tracker even after a stop/start cycle.
let currentEngagementTracker: ReturnType<typeof initEngagementTracking> | null = null
let routeChangeRegistered = false
let activeCleanups: Array<() => void> = []

/**
 * One-time setup: inject the Jitsu script and wait for it to load. Does not start any listeners or
 * send any events. Safe to call regardless of consent state — Jitsu is initOnly and silent until we
 * act.
 */
export const initJitsu = async ({ tenantId }: InitJitsuParams): Promise<boolean> => {
  const jitsuInitPromise = new Promise<void>(resolve => {
    window.__cpJitsuOnInit = resolve
    window.jitsuConfig = {
      defaultPayloadContext: { tenantId },
      initOnly: true,
      onload: '__cpJitsuOnInit',
      debug: getMode() === 'verbose',
      fetch: (url: RequestInfo | URL, init?: RequestInit) =>
        fetch(url, { ...init, keepalive: true }),
    }
  })

  let hasLoadScriptError = false

  await injectScript(environment.getTrackingUrl(getEnv('DEPLOY_ENV')), {
    data: {
      'write-key': getEnv('REACT_APP_JITSU_WRITE_KEY'),
    },
  }).catch(error => {
    hasLoadScriptError = true
    const errorMessage = `Failed to inject cpcdp snippet: ${error.message}`
    if (getEnv('DEPLOY_ENV') === 'development') {
      logger.warn(errorMessage)
    } else {
      logger.error(errorMessage)
    }
  })

  if (hasLoadScriptError) return false

  await withTimeout(jitsuInitPromise, 2_000, 'Jitsu init timed out').catch(error => {
    const errorMessage = `Failed to init jitsu: ${error.message}`
    if (getEnv('DEPLOY_ENV') === 'development') {
      logger.warn(errorMessage)
    } else {
      logger.error(errorMessage)
    }
  })

  if (jitsu.isReady()) {
    flushQueue()
  }

  return jitsu.isReady()
}

/**
 * Start analytics listeners: engagement, route change, file downloads, form tracking. Sends the
 * initial page view. Safe to call multiple times — previous listeners are stopped first.
 */
export const startAnalyticsListeners = (): void => {
  if (!jitsu.isReady()) return

  // Stop any previously active listeners before re-registering
  stopAnalyticsListeners()

  const engagementTracker = initEngagementTracking(({ engagementMsec }) => {
    jitsu.track('Engagement', { engagementMsec })
  })
  currentEngagementTracker = engagementTracker
  activeCleanups.push(() => {
    engagementTracker.stop()
    currentEngagementTracker = null
  })

  // Register route change handler once — references currentEngagementTracker
  // so it stays correct across stop/start cycles without double-registration.
  if (!routeChangeRegistered) {
    routeChangeRegistered = true
    onRouteChange(() => {
      currentEngagementTracker?.flush()
      jitsu.page()
    })
  }

  jitsu.page()

  const stopFileDownloadTracking = initFileDownloadTracking(
    ({ file_extension, file_name, link_text, link_url }) => {
      jitsu.track('FileDownload', { file_extension, file_name, link_text, link_url })
    }
  )
  activeCleanups.push(stopFileDownloadTracking)

  const stopFormTracking = initFormTracking(form => {
    trackEmailFromForm(form, ({ email, formId, formName, emailFieldName, emailFieldId }) => {
      const user = jitsu.user()
      jitsu.identify(user?.userId ?? user?.anonymousId, { email })
      jitsu.track('FormSubmit', {
        email,
        formId,
        formName,
        emailFieldName,
        emailFieldId,
      })
    })
  })
  activeCleanups.push(stopFormTracking)
}

/** Stop all active analytics listeners. Jitsu script stays loaded. */
export const stopAnalyticsListeners = (): void => {
  activeCleanups.forEach(fn => fn())
  activeCleanups = []
}
